// src/core/bewirtschaftung.ts
//
// Baustein 5 ("Bewirtschaftung Pauschal/Einzeln", Korrektur von Dominiks frueherer
// "3 % uebernehmen"-Vorgabe, die als fachlich falsch verworfen wurde): gemeinsame
// Bausteine fuer den neuen Pauschal/Einzeln-Umschalter in den 5 "Annahmen pruefen"-
// Formularen (MFH/EFH/Gewerbe/Gemischt/ETW).
//
// Pauschal: ein einziger Wert = Bewirtschaftungspauschale x Kaltmiete (Saetze bereits in
// core/score/dealScoreConfig.ts als BEWIRTSCHAFTUNG_PAUSCHALE_PCT vorhanden -- hier NICHT
// dupliziert). Score-Logik im Pauschal-Pfad bleibt unveraendert (dieselbe (1-Pauschale)-
// NOI-Formel, die /check ueber quickCheckGenericAdapter.ts schon fuer den Schnell-Check
// verwendet).
//
// Einzeln: getrennte Felder (nicht umlagefaehige Betriebskosten, Verwaltung, Instandhaltung,
// Mietausfall). Instandhaltung bekommt dort einen Vorschlag nach § 28 II. BV (Zweite
// Berechnungsverordnung), gestaffelt nach Gebaeudealter + Aufzug-Zuschlag -- gilt nur fuer
// Wohnraum (nicht Gewerbe).
export type BewirtschaftungModus = "pauschal" | "einzeln";

/**
 * § 28 II. BV -- Richtwerte fuer die Instandhaltungsruecklage in €/m² Wohnflaeche und Jahr,
 * gestaffelt nach Gebaeudealter (Bezugsjahr = Baujahr), plus Zuschlag bei Aufzug. Stand
 * 1.1.2026, alle 3 Jahre neu zu pruefen (naechste Pruefung 2029) -- bei Aktualisierung bitte
 * `stand`/`naechstePruefung` mitfuehren, damit das Alter dieses Richtwerts im Formular/in
 * DEAL-FLOW-STATUS.md nachvollziehbar bleibt.
 */
export const INSTANDHALTUNG_RICHTWERT_BV = {
  stand: "2026-01-01",
  naechstePruefung: "2029-01-01",
  quelle: "Richtwert nach II. Berechnungsverordnung (§ 28 II. BV)",
  proM2Jahr: {
    unter22Jahre: 11.5,
    ab22Jahre: 14.58,
    ab32Jahre: 18.62,
  },
  aufzugZuschlagProM2Jahr: 1.63,
} as const;

export type InstandhaltungStufe = "unter22Jahre" | "ab22Jahre" | "ab32Jahre";

/** Altersstufe aus dem Baujahr (Stichjahr = aktuelles Jahr). Ohne Baujahr: mittlere Stufe
 *  (ab22Jahre) als vorsichtiger Default, `baujahrFehlt: true` fuer den Formular-Hinweis. */
export function instandhaltungStufeFromBaujahr(
  baujahr: number | null | undefined,
  bezugsjahr: number = new Date().getFullYear()
): { stufe: InstandhaltungStufe; baujahrFehlt: boolean } {
  if (baujahr == null || !Number.isFinite(baujahr) || baujahr <= 0) {
    return { stufe: "ab22Jahre", baujahrFehlt: true };
  }
  const alter = bezugsjahr - baujahr;
  if (alter >= 32) return { stufe: "ab32Jahre", baujahrFehlt: false };
  if (alter >= 22) return { stufe: "ab22Jahre", baujahrFehlt: false };
  return { stufe: "unter22Jahre", baujahrFehlt: false };
}

/** Kompletter Instandhaltungs-Richtwert (€/m²/Jahr) inkl. Aufzug-Zuschlag, fuer den
 *  "Vorschlag uebernehmen"-Button im Einzeln-Modus (Wohnraum, nicht Gewerbe). */
export function instandhaltungRichtwertProM2(
  baujahr: number | null | undefined,
  hatAufzug: boolean,
  bezugsjahr: number = new Date().getFullYear()
): { wertProM2Jahr: number; stufe: InstandhaltungStufe; baujahrFehlt: boolean } {
  const { stufe, baujahrFehlt } = instandhaltungStufeFromBaujahr(baujahr, bezugsjahr);
  const basis = INSTANDHALTUNG_RICHTWERT_BV.proM2Jahr[stufe];
  const wertProM2Jahr = basis + (hatAufzug ? INSTANDHALTUNG_RICHTWERT_BV.aufzugZuschlagProM2Jahr : 0);
  return { wertProM2Jahr, stufe, baujahrFehlt };
}

/**
 * Snapshot-Migration (Dominiks Vorgabe 17.10.2026, zweifach nachkorrigiert am selben Tag):
 * Grundsatz -- die Migration darf Score/Cashflow/Kosten eines gespeicherten Objekts NIE
 * veraendern, auch nicht unter Stress (Zielsuche/Stresstest-Pillar).
 *
 * Nachkorrektur 2 (Live-Befund nach dem Push, Score 69->70 bei einem realen MFH-Objekt mit
 * EK-Luecke): die ERSTE Nachkorrektur (unten) prüfte nur den BASIS-Fall (nicht-umlagefaehige
 * Kosten == Pauschale x Kaltmiete) -- das reicht NICHT, wenn das Legacy-Modell des Typs
 * nicht-umlagefaehige Kosten als FIXEN €-Betrag behandelt (MFH/EFH: core/metrics/mfh.ts bzw.
 * efhDealScore.ts, Legacy-Zweig). Unter Stress (Miete -10 %) bleibt der Legacy-Fixbetrag
 * gleich, waehrend "Pauschal" (effRentYear x Pauschale%) mit der gestressten Miete
 * MITSKALIERT -- das Objekt wirkt unter Stress rechnerisch robuster, als die alte Formel je
 * zeigte, selbst wenn der Basisbetrag exakt passt. Fuer MFH/EFH gibt es deshalb KEINEN
 * Basisfall, in dem "Pauschal" bei der Migration wirklich stress-neutral ist -- `erlaubePauschal`
 * (von den Aufrufern gesetzt: false bei MFH/EFH, true bei Gewerbe, dessen Legacy-Opex-Modell
 * bereits prozentual ist und selbst mit der gestressten Miete skaliert, siehe
 * gewerbeDealScore.ts) schaltet den Basisfall-Abgleich unten komplett ab, migriert dann immer
 * zu "einzeln" -- exakt das bereits etablierte, sichere Verhalten von Gemischt.
 *
 * Nachkorrektur 1 (Pre-Push-Befund desselben Tages): ein alter Snapshot ohne gespeicherten
 * Modus wird NUR dann "pauschal" (und nur, wenn `erlaubePauschal`), wenn die Pauschal-
 * Rechnung nachweislich EXAKT dieselben Kosten ergibt wie die bisherige Rechnung (gespeicherte
 * nicht-umlagefaehige Kosten == Pauschale x Kaltmiete, Rundungstoleranz 1 €, UND
 * Instandhaltung/Capex == 0) -- sonst "einzeln", mit den gespeicherten Werten 1:1 uebernommen
 * (nichts geht verloren).
 *
 * `migriertVonAlt: true` steuert den Lade-Hinweis im Formular ("Dein bisheriger ... wurde
 * uebernommen") -- nur gesetzt, wenn tatsaechlich etwas aus dem Alt-Bestand uebernommen wurde
 * (nicht, wenn der Modus explizit gespeichert war oder die Pauschale zufaellig schon passte).
 *
 * Hinweis zur "instandhaltungVermutlichEnthalten"-Anzeige (ob die nicht-umlagefaehigen Kosten
 * die Pauschale schon abdecken, siehe BewirtschaftungPauschalEinzeln.tsx' neutraler Ersatz-
 * Hinweis): das wird bewusst NICHT hier, sondern je Typ NACH dieser Funktion berechnet (aus
 * dem tatsaechlichen Endzustand instandhaltungJeM2Jahr/nichtUmlagefaehig) -- gilt dann
 * gleichermassen fuer migrierte UND explizit gespeicherte "Einzeln"-Snapshots (z. B. aus
 * /check), nicht nur fuer den Migrations-Fall.
 */
export function resolveBewirtschaftungModus(
  savedModus: unknown,
  legacyInstandhaltungValue: number,
  legacyNichtUmlagefaehigValue: number,
  pauschaleBetrag: number,
  erlaubePauschal: boolean = true
): { modus: BewirtschaftungModus; migriertVonAlt: boolean } {
  if (savedModus === "pauschal" || savedModus === "einzeln") {
    return { modus: savedModus, migriertVonAlt: false };
  }
  const entsprichtPauschale =
    erlaubePauschal &&
    legacyInstandhaltungValue === 0 &&
    Math.abs(legacyNichtUmlagefaehigValue - pauschaleBetrag) <= 1;
  if (entsprichtPauschale) {
    return { modus: "pauschal", migriertVonAlt: false };
  }
  return { modus: "einzeln", migriertVonAlt: true };
}
